<template>
  <div class="flex flex-col bg-surface-1 border border-border-separator rounded-md overflow-hidden">
    <!-- overflow-hidden: below the width where even the wrapped controls no longer fit, they run off the right
         edge and are clipped by the card's border instead of being drawn out over whatever is beside it. -->
    <!-- Wraps: when the panel is too narrow for the name and the controls side by side, the controls drop
         to their own line (right-aligned) instead of the name block being squeezed to nothing, which let
         its badges spill over the switch. The name block's floor is what decides when that happens. -->
    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 p-3">
      <div class="flex-1 min-w-[9rem]">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-sm font-medium text-text-default truncate">{{ task.name || $t('scheduled.tasks.untitledTask') }}</span>
          <!-- A badge for every type, not only Watcher. The row used to name the one type it considered
               surprising, which left a recap and a morning brief looking like the same thing — they
               behave differently enough that the person needs to be able to tell them apart at rest. -->
          <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-subtle text-text-link flex-shrink-0">
            {{ type_label }}
          </span>
          <!-- The store the task actually runs against. It has always been on the record and never on
               the row, so a multi-rooftop user could not tell two identically named tasks apart. -->
          <span
            v-if="store_label"
            class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-2 text-text-subtle flex-shrink-0"
          >{{ store_label }}</span>
          <!-- A spent one-time task stays listed so it can be found and deleted, and this says why it is
               off: it ran (or its moment passed) and cannot be switched back on. -->
          <span
            v-if="is_expired"
            class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-2 text-text-subtle flex-shrink-0"
            data-test="expired-badge"
          >{{ $t('scheduled.tasks.expired') }}</span>
        </div>
        <!-- The schedule and the next occurrence of it on one line, which is where they belong: the
             second is an instance of the first, and it used to sit on a row of its own repeating the
             clock time the sentence had already given.

             Just the day, then — but only when that sentence actually states the time, which is what
             schedule_states_time decides. And withheld entirely when there is no next run rather
             than printing an em dash: an event-driven task has no scheduled time by definition. -->
        <div v-if="!is_event_driven" class="text-xs text-text-subtle truncate mt-1" data-test="schedule-line">
          {{ schedule_summary }}
          <span v-if="show_next_run">· {{ $t('scheduled.tasks.next') }} {{ next_run_display }}</span>
        </div>
      </div>

      <!-- The controls travel as one group so that, when the row wraps, they wrap together rather than
           the last icon or two being left alone on a line of their own. -->
      <div class="flex items-center gap-3 flex-shrink-0 ml-auto">
      <!-- Enable / pause -->
      <!-- Not for an expired one-time task: the server refuses to resume it. -->
      <div v-if="!is_expired" class="flex items-center gap-1 flex-shrink-0" :title="task.enabled ? $t('scheduled.tasks.enabledTip') : $t('scheduled.tasks.pausedTip')">
        <fusion-switch :modelValue="task.enabled" :disabled="busy || isReadOnly" @update:modelValue="$emit('toggle-enabled', task)" />
      </div>

      <!-- Run now — withdrawn outside the beta: this is the control that used to answer 202 and
           then deliver nothing at all, because the dispatcher declined the store on waking -->
      <!-- Never on a one-time task: it runs only at its own time, and the server refuses it. -->
      <div
        v-if="!betaClosed && !is_one_time"
        class="h-8 w-8 flex items-center justify-center rounded hover:bg-surface-3 flex-shrink-0"
        :class="(busy || isReadOnly) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer text-text-link'"
        :title="$t('scheduled.tasks.runNow')"
        @click="!busy && !isReadOnly && $emit('run-now', task)"
      >
        <i class="ri-play-line ri-125" />
      </div>

      <!-- Edit — withdrawn on a rooftop outside the beta, where every save would be refused -->
      <!-- Never on a one-time task: the editor does not offer the cadence (they are created in chat). -->
      <div
        v-if="!betaClosed && !is_one_time"
        class="h-8 w-8 flex items-center justify-center rounded hover:bg-surface-3 flex-shrink-0"
        :class="(busy || isReadOnly) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer text-text-link'"
        :title="$t('scheduled.tasks.edit')"
        @click="!busy && !isReadOnly && $emit('edit', task)"
      >
        <i class="ri-pencil-line ri-125" />
      </div>

      <!-- Run history -->
      <div
        class="h-8 w-8 flex items-center justify-center rounded hover:bg-surface-3 flex-shrink-0 cursor-pointer text-text-link"
        :title="$t('scheduled.tasks.runHistory')"
        @click="$emit('toggle-history', task)"
      >
        <i class="ri-history-line ri-125" />
      </div>

      <!-- Delete (inline confirm) -->
      <div class="flex-shrink-0" @click.stop>
        <div
          v-if="!confirming"
          class="h-8 w-8 flex items-center justify-center rounded hover:bg-surface-3"
          :class="(busy || isReadOnly) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer text-danger'"
          :title="$t('scheduled.tasks.delete')"
          @click="!busy && !isReadOnly && (confirming = true)"
        >
          <i class="ri-delete-bin-line ri-125" />
        </div>
        <!-- The confirm buttons are about three icons wide, and everything on this line is flex-shrink-0
             but the name block, so putting them here crushed the name to nothing and the badges spilled
             over the switch. They are on the line below instead; this keeps the icon's footprint so the
             controls do not shift while the question is open. -->
        <div v-else class="h-8 w-8" />
      </div>
      </div>
    </div>

    <!-- Open results. Its own line beneath the controls rather than another icon among them: it is the
         one control here that navigates somewhere instead of acting on the task, and the icon row was
         already five deep at this width. -->
    <div class="flex items-center gap-2 px-3 pb-2.5 -mt-1">
      <!-- While the delete question is open it takes this line over: Cancel and Delete, right-aligned
           under the icon they answer. Open results and the last-run text come back when it closes. -->
      <div v-if="confirming" class="flex items-center gap-1 ml-auto" @click.stop>
        <button class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link" @click="confirming = false">{{ $t('common.cancel') }}</button>
        <button class="text-xs px-2 py-1 rounded bg-btn-primary text-white" @click="confirmDelete">{{ $t('scheduled.tasks.delete') }}</button>
      </div>
      <template v-else>
      <button
        class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link flex-shrink-0"
        @click="$emit('open-results', task)"
      >Open results</button>
      <!-- The outcome moved here from the Next line, and leads: it is the one coloured word on the
           row, and a list is read down the left of this line rather than along it. When it ran only
           qualifies it, so it follows. -->
      <span class="flex items-center gap-1.5 min-w-0">
        <span
          v-if="task.lastRunStatus"
          class="text-xs flex-shrink-0"
          :class="statusClass(task.lastRunStatus)"
        >{{ task.lastRunStatus }}</span>
        <span class="text-xs text-text-subtle truncate">{{ last_run_label }}</span>
      </span>
      </template>
    </div>

    <!-- Run history + activity panel -->
    <div v-if="historyOpen" class="px-3 pb-3 border-t border-border-separator">
      <div v-if="historyProcessing" class="py-2 text-xs text-text-subtle">{{ $t('scheduled.tasks.loadingHistory') }}</div>
      <template v-else>
        <div class="pt-2 text-[11px] uppercase tracking-wide text-text-subtle">{{ $t('scheduled.tasks.runsHeader') }}</div>
        <div v-if="!historyRuns.length" class="py-1 text-xs text-text-subtle">{{ $t('scheduled.tasks.noRuns') }}</div>
        <div v-else class="flex flex-col gap-1">
          <div v-for="run in historyRuns" :key="run.id" class="flex items-center gap-2 text-xs">
            <span :class="statusClass(run.status)">{{ run.status }}</span>
            <span class="text-text-subtle">{{ formatDateTime(run.ranAt || run.startedAt) }}</span>
            <span v-if="run.detail" class="text-text-subtle truncate">· {{ run.detail }}</span>
          </div>
        </div>

        <div class="pt-2 text-[11px] uppercase tracking-wide text-text-subtle">{{ $t('scheduled.tasks.activityHeader') }}</div>
        <div v-if="!historyAudit.length" class="py-1 text-xs text-text-subtle">{{ $t('scheduled.tasks.noActivity') }}</div>
        <div v-else class="flex flex-col gap-1">
          <div v-for="entry in historyAudit" :key="entry.id" class="flex items-center gap-2 text-xs">
            <span class="text-text-default">{{ entry.action }}</span>
            <span class="text-text-subtle">{{ formatDateTime(entry.at) }}</span>
            <span class="text-text-subtle truncate">· {{ entry.actor }}</span>
            <span v-if="entry.detail" class="text-text-subtle truncate">· {{ entry.detail }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import FusionSwitch from '@components/UI/FusionSwitch.vue';
import { describeAnchor, isAnchored } from '../../utils/storeHoursAnchor';

const TYPE_LABELS = {
  // The dealership-facing word on the left of each pair, the enum's on the right. "Recap" is what the
  // self-serve manager calls a ScheduledQuestion; nothing renders the enum name to a user.
  ScheduledQuestion: 'Recap',
  Watcher: 'Watcher',
  MorningBrief: 'Morning brief',
};

export default {
  name: 'ScheduledTaskRow',
  components: {
    'fusion-switch': FusionSwitch,
  },
  props: {
    task: { type: Object, required: true },
    busy: { type: Boolean, required: false, default: false },
    betaClosed: { type: Boolean, required: false, default: false },
    isReadOnly: { type: Boolean, required: false, default: false },
    // History stays owned by the parent so that only one row can be expanded at a time, which is the
    // behaviour the single `history_open_id` gave before this row was its own component.
    historyOpen: { type: Boolean, required: false, default: false },
    historyRuns: { type: Array, required: false, default: () => [] },
    historyAudit: { type: Array, required: false, default: () => [] },
    historyProcessing: { type: Boolean, required: false, default: false },
  },
  emits: ['toggle-enabled', 'run-now', 'edit', 'toggle-history', 'delete', 'open-results'],
  data() {
    return {
      confirming: false,
    };
  },
  computed: {
    ...mapStores(useMainStore),
    all_dealers() {
      return this.mainStore.all_dealers;
    },
    // Checked before the type: an event task is stored as a ScheduledQuestion (ScheduledCreate says why),
    // so the type alone would badge it "Recap". Case-insensitive, the same test ScheduledEdit and the
    // server's TaskTrigger.IsEventDriven make.
    is_event_driven() {
      return String((this.task.trigger && this.task.trigger.type) || '').toLowerCase() === 'event';
    },
    is_one_time() {
      return String((this.task.trigger && this.task.trigger.cadence) || '').toLowerCase() === 'onetime';
    },
    /**
     * A one-time task that is off and will never run again: it has run, or its one moment has passed
     * (which also covers one written before it carried a date — its nextRunAt is 0001-01-01). A one-time
     * task paused while its date is still ahead is not expired; it can be switched back on.
     */
    is_expired() {
      if (!this.is_one_time || this.task.enabled) return false;
      if (this.task.lastRunAt) return true;
      const at = this.task.trigger && this.task.trigger.nextRunAt;
      const ms = at ? Date.parse(at) : NaN;
      return Number.isNaN(ms) || ms <= Date.now();
    },
    type_label() {
      if (this.is_event_driven) return 'Event';
      return TYPE_LABELS[this.task.taskType] || this.task.taskType || 'Recap';
    },
    /**
     * Whether the schedule line above already names a clock time.
     *
     * Three shapes of it do not, and for each the time is then on the card nowhere else — so the
     * "Next" line is the only place it can be read and must keep it. Taken from the trigger rather
     * than by looking for digits in the rendered sentence, because the server's CadenceDescriber
     * decides the wording from these same fields:
     *
     *  - Hourly renders "Every hour" / "Every hour at :30". Its own comment is explicit that the
     *    hour-of-day is left out on purpose, an hourly task having twenty-four of them.
     *  - An anchored task renders "30 minutes after open" — it resolves one fire time a day from the
     *    store's calendar, so there is no fixed clock value to have printed.
     *  - A trigger with no time renders a bare "Every day", the describer's WhenSuffix being empty.
     */
    schedule_states_time() {
      const t = this.task.trigger;
      // Interval (NPM-225) is a period like Hourly: "Every 5 minutes" states no clock time either.
      if (!t || t.cadence === 'Hourly' || t.cadence === 'Interval') return false;
      if (isAnchored(t.storeHoursAnchor)) return false;
      return !!t.time;
    },
    // Never for an event task: NextRunAt is a non-nullable DateTime on the server, so one with no clock
    // arrives as 0001-01-01 and would print "Next: 1/1".
    show_next_run() {
      if (this.is_event_driven) return false;
      // A one-time task's schedule line already names its date and time ("Once on Thu, Oct 15 at 3:35 PM"),
      // and once spent its nextRunAt is in the past — "Next" would be a run that is not coming.
      if (this.is_one_time) return false;
      return !!(this.task.trigger && this.task.trigger.nextRunAt);
    },
    next_run_display() {
      const at = this.task.trigger && this.task.trigger.nextRunAt;
      return this.formatDateTime(at, this.schedule_states_time ? 'M/D' : 'M/D h:mm A');
    },
    /**
     * When it last ran, worded to sit after the status word rather than instead of it. The leading
     * separator belongs to the label and not to the status span so that it stays subtle — a green
     * middot beside a red one would read as part of the outcome.
     */
    last_run_label() {
      if (!this.task.lastRunAt) return this.task.lastRunStatus ? '' : 'Not run yet';
      const prefix = this.task.lastRunStatus ? '· ' : '';
      return `${prefix}Last run ${this.formatDateTime(this.task.lastRunAt)}`;
    },
    store_label() {
      const dealers = Array.isArray(this.all_dealers) ? this.all_dealers : [];
      const dealer = dealers.find(d => d && d.cifNumber === this.task.dealerId);
      // The bare CIF when the name is not to hand, never nothing: the store is what distinguishes two
      // identically named tasks, so an unresolved name must still say WHICH store.
      return dealer?.name || this.task.dealerId || '';
    },
    /**
     * The schedule in plain language, rendered by the server.
     *
     * `scheduleSummary` comes from CadenceDescriber, which describes itself as the single place these
     * sentences are produced "so the editor, the list, and conversation all agree". The local fallback
     * below is for a task object that predates the field — a create response held in memory, or an older
     * cached list — and not a second opinion: if both are reachable the server's wins.
     */
    schedule_summary() {
      // Ahead of the server's sentence, which is wrong for this case: CadenceDescriber does not ask
      // IsEventDriven, so it describes the Daily/07:00 an event task is left holding ("Every day at
      // 7:00 AM") — a schedule the task never runs on.
      if (this.is_event_driven) return '';
      if (this.task.scheduleSummary) return this.task.scheduleSummary;

      const t = this.task.trigger;
      if (!t) return '';
      // NPM-225: an interval has no clock time; the 07:00 it still holds is not when it runs.
      if (t.cadence === 'Interval') return this.$t('scheduled.common.cadenceInterval', { minutes: t.intervalMinutes ?? '?' });
      const parts = [t.cadence || 'Daily'];
      // An anchored task resolves its fire time per day from the store's hours, so the stored clock value
      // is meaningless for it — printing "at 07:00" would describe a schedule its owner never chose.
      if (isAnchored(t.storeHoursAnchor)) parts.push(describeAnchor(t.storeHoursAnchor, t.anchorOffsetMinutes));
      else if (t.time) parts.push(this.$t('scheduled.tasks.summaryAt', { time: t.time }));
      return parts.join(' ');
    },
  },
  methods: {
    confirmDelete() {
      this.confirming = false;
      this.$emit('delete', this.task);
    },
    /**
     * `format` is optional and defaults to the full date-and-time every caller here used to get.
     *
     * TWO BUGS FIXED IN THIS CALL, both of which made it a no-op:
     *
     *  1. $FUSdateFormat's signature is (format, dateInput) — NOT (dateInput, format). This passed
     *     them the wrong way round, so the plugin took 'MMM d, h:mm a' as the DATE, built an
     *     Invalid Date from it, and returned ''. Falsy, so every timestamp on this row fell through
     *     to the toLocaleString() fallback below.
     *  2. 'MMM d, h:mm a' was never a format this plugin understands. Its vocabulary is
     *     YYYY/YY/MM/M/DD/D/HH/H/hh/h/mm/m/ss/s/A/a — there is no month-name token and no lowercase
     *     'd', so the string could not have worked in either argument order. It reads like
     *     date-fns, which this app does not use here.
     *
     * The upshot is that the card has always rendered '9/22/2026, 10:30:00 AM' from the fallback
     * rather than the short form the format string was reaching for. M/D matches how
     * MessageTimestamp and ReyConversationList already write dates.
     */
    formatDateTime(value, format) {
      if (!value) return '—';
      if (this.$FUSdateFormat) {
        const formatted = this.$FUSdateFormat(format || 'M/D h:mm A', value);
        if (formatted) return formatted;
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? '—' : d.toLocaleString();
    },
    statusClass(status) {
      if (status === 'Failed') return 'text-danger';
      // Was text-text-link, which rendered a delivered run in the same indigo as every clickable
      // thing on the panel — a success that reads as a hyperlink. --text-success is the semantic
      // token for this and is theme-aware (green-70 in light, green-50 in dark), where the raw
      // `success` primitive would be ~2.9:1 on a light card.
      if (status === 'Delivered') return 'text-text-success';
      // Skipped did not run and AwaitingUser is holding a question nobody has answered — both want
      // the owner's eye, neither is a failure. NothingToReport falls through to subtle on purpose:
      // a quiet watcher is the watcher working.
      if (status === 'Skipped' || status === 'AwaitingUser') return 'text-text-warning';
      return 'text-text-subtle';
    },
  },
};
</script>
